import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Icon from "../../../components/common/Icon";
import Button from "../../../components/common/Button";
import GlassCarousel from "../../../components/common/GlassCarousel";
import Reveal from "../../../components/common/Reveal";
import { getPublicClubsApi } from "../../../api/match.api";

function getItems(response) {
  const value = response?.data?.data;

  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.clubs)) return value.clubs;
  if (Array.isArray(value?.results)) return value.results;

  return [];
}

function getImageUrl(path) {
  if (!path) return null;

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL?.replace("/api/v1", "") || "";

  return `${base}${path}`;
}

function getClubName(club) {
  return club?.name || club?.title || club?.clubName || "باشگاه فوتبال";
}

function getClubLogo(club) {
  return (
    club?.logo ||
    club?.logoUrl ||
    club?.image ||
    club?.imageUrl ||
    club?.avatar ||
    null
  );
}

function getClubCity(club) {
  return (
    club?.city?.name ||
    club?.cityName ||
    club?.location ||
    club?.province?.name ||
    "فوتبال شهرستان پاوه"
  );
}

function getClubId(club) {
  return club?.id || club?._id || club?.slug;
}

function ClubCard({ club }) {
  const name = getClubName(club);
  const logo = getImageUrl(getClubLogo(club));
  const city = getClubCity(club);
  const id = getClubId(club);

  return (
    <Link
      to={id ? `/clubs/${id}` : "/clubs"}
      className="glass group relative block overflow-hidden rounded-[2rem] p-6 transition-all duration-500 hover:shadow-card-hover"
    >
      <div className="absolute -right-16 -top-16 h-36 w-36 rounded-full bg-floodlight/30 blur-xl transition-transform duration-700 group-hover:scale-[2]" />

      <div className="relative flex items-center gap-4">
        <div className="grid h-[72px] w-[72px] shrink-0 place-items-center overflow-hidden rounded-[1.4rem] border border-white bg-gradient-to-br from-white to-slate-100 shadow-md transition-transform duration-500 group-hover:scale-105">
          {logo ? (
            <img
              src={logo}
              alt={name}
              loading="lazy"
              className="h-full w-full object-contain p-2"
            />
          ) : (
            <Icon
              name="shield"
              className="h-7 w-7 text-emerald-500/50"
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-black text-slate-900 transition-colors group-hover:text-emerald-600">
            {name}
          </h3>

          <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
            <Icon
              name="map-pin"
              className="h-3.5 w-3.5 text-emerald-500"
            />
            <span className="truncate">{city}</span>
          </div>
        </div>

        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/80 text-slate-400 transition-all duration-300 group-hover:bg-ink group-hover:text-floodlight">
          <Icon name="arrowLeft" className="h-4 w-4" />
        </div>
      </div>

      <div className="relative mt-5 flex items-center justify-between border-t border-slate-200/70 pt-4">
        <span className="text-[10px] font-black text-slate-400">
          باشگاه عضو سامانه
        </span>

        <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-emerald-600">
          مشاهده پروفایل
          <Icon name="arrowLeft" className="h-3 w-3" />
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-1 origin-right scale-x-0 bg-gradient-to-l from-emerald-500 to-floodlight transition-transform duration-500 group-hover:scale-x-100" />
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-[1.75rem] border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-slate-100" />

        <div className="flex-1 space-y-3">
          <div className="h-4 w-3/4 rounded bg-slate-100" />
          <div className="h-3 w-1/2 rounded bg-slate-100" />
        </div>

        <div className="h-9 w-9 rounded-full bg-slate-100" />
      </div>

      <div className="mt-5 h-3 w-full rounded bg-slate-100" />
    </div>
  );
}

export default function FeaturedClubs() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    getPublicClubsApi({
      limit: 6,
      page: 1,
    })
      .then((response) => {
        if (!mounted) return;

        setClubs(getItems(response).slice(0, 6));
      })
      .catch(() => {
        if (mounted) {
          setClubs([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="relative overflow-hidden py-20 sm:py-24 lg:py-28">
      <div className="pointer-events-none absolute right-[-180px] top-20 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />

      <div className="pointer-events-none absolute left-[-180px] bottom-0 h-96 w-96 rounded-full bg-blue-400/10 blur-3xl" />

      <div className="container-page relative">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <span className="section-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              جامعه فوتبال شهرستان پاوه
            </span>

            <h2 className="section-title mt-5">
              باشگاه‌های
              <span className="text-gradient"> عضو سامانه</span>
            </h2>

            <p className="mt-4 max-w-xl text-sm leading-8 text-slate-500 sm:text-base">
              باشگاه‌های رسمی و فعال فوتبال شهر را بشناسید و اطلاعات
              اختصاصی هر باشگاه را مشاهده کنید.
            </p>
          </div>

          <Button
            as={Link}
            to="/clubs"
            variant="outlineDark"
            size="sm"
            className="w-fit shrink-0"
          >
            مشاهده همه باشگاه‌ها
            <Icon name="arrowLeft" className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <SkeletonCard key={item} />
            ))}
          </div>
        ) : clubs.length === 0 ? (
          <div className="glass mt-12 rounded-[2rem] border-dashed px-6 py-20 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-white text-slate-300 shadow-sm">
              <Icon name="shield" className="h-8 w-8" />
            </div>

            <h3 className="mt-5 text-lg font-black text-slate-700">
              باشگاهی برای نمایش وجود ندارد
            </h3>

            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-slate-400">
              اطلاعات باشگاه‌های عضو پس از ثبت و تأیید در این بخش نمایش
              داده خواهد شد.
            </p>

            <Button
              as={Link}
              to="/clubs"
              variant="outlineDark"
              size="sm"
              className="mt-6"
            >
              صفحه باشگاه‌ها
              <Icon name="arrowLeft" className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Reveal className="mt-14">
            <GlassCarousel>
              {clubs.map((club) => (
                <ClubCard
                  key={getClubId(club) || getClubName(club)}
                  club={club}
                />
              ))}
            </GlassCarousel>
          </Reveal>
        )}
      </div>
    </section>
  );
}